import React, { useMemo } from 'react';
import { Icon, Bubble, Button, Checkbox } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import RenderGameIcon from '@op/components/RenderImg/renderGameIcon';
import useLanguage from '@op/hooks/useLanguage';
import useGetFilterKeywordList from '@op/hooks/useGetFilterKeywordList';
import './index.scss';
import BubbleCommon from '@op/components/BubbleCommon';
import { KeywordUrlTabType } from '../../types';
import type { UrlPercentType } from '../../hook';
import { useIntl } from 'umi';
import { DBO_U } from '@op/utils/format';
import moment from 'moment';

interface IAnalysisView {
  wholeGameInfo: any;
  gameInfo: any;
  filter: any;
  children?: React.ReactNode;
  isPreviews?: boolean;
  tab: KeywordUrlTabType;
  percentList?: Array<UrlPercentType>;
  isSingleUrl?: boolean;
  checked?: boolean;
  setChecked?: (flag: boolean) => void;
  showCheckbox?: boolean;
  isTopicAnalysis?: boolean;
}
export default function AnalysisView({
  filter,
  wholeGameInfo,
  gameInfo,
  children,
  tab,
  percentList,
  isSingleUrl,
  checked,
  setChecked,
  showCheckbox,
  isTopicAnalysis,
}: IAnalysisView) {
  const { entity_image, entity_name } = wholeGameInfo || {};

  if (filter === null) return null;

  return (
    <div id="dbo-analysis-view-screen-box">
      <CardCommon className="dbo-analysis-view">
        <div
          className="dbo-analysis-view-background"
          style={{ backgroundImage: `url(${entity_image})` }}
        />
        <div className="dbo-analysis-view-mask"></div>
        <AnalysisViewHeader
          wholeGameInfo={wholeGameInfo}
          entity_name={entity_name}
          filter={filter}
          gameInfo={gameInfo}
          tab={tab}
          percentList={percentList}
          isSingleUrl={isSingleUrl}
          setChecked={setChecked}
          isTopicAnalysis={isTopicAnalysis}
          checked={checked}
          showCheckbox={showCheckbox}
        ></AnalysisViewHeader>
        <>{children}</>
      </CardCommon>
    </div>
  );
}

function AnalysisViewHeader({
  wholeGameInfo,
  entity_name,
  filter,
  gameInfo,
  tab,
  percentList,
  isSingleUrl,
  isTopicAnalysis,
  checked,
  setChecked,
  showCheckbox,
}: {
  wholeGameInfo: any;
  entity_name: string;
  filter: any;
  gameInfo: any;
  tab: KeywordUrlTabType;
  percentList?: Array<UrlPercentType>;
  isSingleUrl?: boolean;
  checked?: boolean;
  setChecked?: (flag: boolean) => void;
  showCheckbox?: boolean;
  isTopicAnalysis?: boolean;
}) {
  const { formatMessage } = useLanguage();

  return (
    <div className="dbo-analysis-view-header">
      <div className="game-info">
        <RenderGameIcon
          gameInfo={wholeGameInfo}
          className="game-icon"
        ></RenderGameIcon>
        <div className="game-details">
          <span className="game-name">{entity_name}</span>

          {tab === 'keyword' ? (
            <HeaderKeywordView
              filter={filter}
              gameInfo={gameInfo}
              isTopicAnalysis={isTopicAnalysis}
              formatMessage={formatMessage}
            ></HeaderKeywordView>
          ) : (
            <HeaderUrlView
              percentList={percentList}
              isSingleUrl={isSingleUrl}
            ></HeaderUrlView>
          )}
        </div>
      </div>
      {
        showCheckbox && (
          <div className="checkbox">
            <Checkbox value={checked} onChange={(v) => setChecked?.(v)}>
              {formatMessage({ id: 'Show All Comments of This Post' })}
            </Checkbox>
          </div>
        )
      }
    </div>
  );
}

/**
 * * URL 的头部
 * @param param0
 * @returns
 */
function HeaderUrlView({
  percentList,
  isSingleUrl,
}: {
  percentList?: Array<UrlPercentType>;
  isSingleUrl?: boolean;
}) {
  const { formatMessage } = useIntl();
  const { successCount, errorCount, item } = useMemo(() => {
    const list = percentList || [];
    let errorCount = 0;
    let successCount = 0;

    list.forEach((item) => {
      if (item.status === 'success') {
        successCount = successCount + 1;
      } else {
        errorCount = errorCount + 1;
      }
    });

    const item = list[0] || {};

    return { successCount, errorCount, item };
  }, [percentList]);

  function getUrlShowText({ status, count, url }: UrlPercentType) {
    if (status === 'success') {
      return formatMessage(
        { id: 'Successfully crawled {count} comments.' },
        { count },
      );
    }

    if (status === 'error' && count > 0) {
      return formatMessage({
        id: 'Scraping failed, the historical data will be displayed by default',
      });
    }

    return formatMessage({ id: 'Failed' });
  }

  function getOneUrlItem(item: UrlPercentType, index: number) {
    const { status, url, comment_time } = item || {};
    const i = index + 1;

    let contentTimeText = null;
    if (comment_time && status === 'success') {
      contentTimeText = `${formatMessage({ id: 'Posting Time' })} ${moment(
        comment_time,
      ).format('YYYY-MM-DD')}`;
    }
    const text = (
      <span className="dbo-bubble-url-item-text">{getUrlShowText(item)}</span>
    );

    return (
      <div className="dbo-bubble-url-item" key={url}>
        <Icon type={status} />
        <div className="dbo-bubble-url-item-top">
          <span className="dbo-bubble-url-item-text">
            URL{i}: {contentTimeText !== null ? contentTimeText : text}
          </span>
          {contentTimeText !== null && (
            <div className="dbo-bubble-url-item-top">{text}</div>
          )}
          <Button type="link" onClick={() => DBO_U.open(url)}>
            {url}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Bubble
      placement="bottom-start"
      dark
      overlayClassName="dbo-bubble-unlimited-width"
      content={
        <div className="dbo-bubble-url">
          {percentList?.map((item, index) => getOneUrlItem(item, index))}
        </div>
      }
    >
      <div className="dbo-analysis-view-urls">
        {isSingleUrl ? (
          <div className="dbo-analysis-view-urls-item">
            <Icon type={item.status === 'success' ? 'success' : 'error'} />
            <span className="dbo-analysis-view-urls-item-text">
              {getUrlShowText(item)}:<span>{item.url}</span>
            </span>
          </div>
        ) : (
          <>
            {successCount > 0 && (
              <div className="dbo-analysis-view-urls-item">
                <Icon type="success" />
                <span className="dbo-analysis-view-urls-item-text">
                  {successCount} URL
                </span>
              </div>
            )}
            {errorCount > 0 && (
              <div className="dbo-analysis-view-urls-item">
                <Icon type="error" />
                <span className="dbo-analysis-view-urls-item-text">
                  {errorCount} URL
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </Bubble>
  );
}

/**
 * Keyword的头部
 * @param param0
 * @returns
 */
function HeaderKeywordView({
  filter,
  gameInfo,
  formatMessage,
  isTopicAnalysis,
}: any) {
  const { loading, renderList } = useGetFilterKeywordList({
    filter,
    gameInfo,
  });



  if (loading || renderList.length === 0) return null;

  return (
    <BubbleCommon
      tipsMap={null}
      showIcon={false}
      content={
        <div className="dbo-scroll-bubble-scroll">
          {formatMessage({ id: 'Selected Keywords' })}
          <br />
          {renderList.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </div>
      }
      overlayClassName="dbo-scroll-bubble"
    >
      <span className="game-keyword">
        {formatMessage({ id: 'Selected Keywords' })}: {renderList.join('; ')}
      </span>
    </BubbleCommon>
  );
}
